import {
  Component,
  ChangeDetectionStrategy,
  inject,
  signal,
  input,
  computed,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { NgIconComponent, provideIcons } from '@ng-icons/core';
import {
  lucideArrowRight,
  lucideArrowLeft,
  lucideCheck,
  lucideMapPin,
  lucideFileSpreadsheet,
  lucidePencilRuler,
  lucideUpload,
  lucideTrash,
  lucideTriangleAlert,
  lucideCircleCheck,
  lucideRocket,
  lucideShieldAlert,
  lucidePhone,
  lucideX,
  lucideHash,
  lucidePlus,
  lucideInfo,
  lucideTag,
  lucideDownload,
  lucideLoader,
  lucideSearch,
  lucideBuilding,
} from '@ng-icons/lucide';
import { HlmInputDirective } from '@spartan-ng/helm/input';
import { BrnDialogImports } from '@spartan-ng/brain/dialog';
import { HlmDialogImports } from '@spartan-ng/helm/dialog';
import { HlmButtonDirective } from '@spartan-ng/helm/button';
import { HlmSeparatorDirective } from '@spartan-ng/helm/separator';
import {
  BaseCreationDialog,
  BaseDialogConfig,
} from '@shared/components/base-creation-dialog/base-creation-dialog';
import { WizardDialogHeader } from '@shared/components/wizard-dialog-header/wizard-dialog-header';
import { WizardDialogFooter } from '@shared/components/wizard-dialog-footer/wizard-dialog-footer';
import { SuffixInput } from '@shared/components/suffix-input/suffix-input';
import { AddressMultiSelect } from '@shared/components/address-multi-select/address-multi-select';
import { FieldShell } from '@shared/components/field-shell/field-shell';
import {
  SummarySection,
  SummaryRow,
} from '@shared/components/summary-section/summary-section';
import { TextareaField } from '@shared/components/textarea-field/textarea-field';
import { StepLead } from '@shared/components/step-lead/step-lead';
import {
  TimelineStep,
  createWizardSteps,
} from '@shared/models/step-timeline.model';
import {
  Campaign,
  CreateCampaignDto,
  CsvValidationResult,
  AddressPreview,
  OutlierAddress,
  SiValidationResult,
  PackageType,
  PackageConfig,
  PackageMeta,
  formatAddressRange,
} from '@features/campaigns/models/campaign.model';
import { CampaignsService } from '@features/campaigns/services/campaigns.service';
import {
  AREA_MAX_KM2,
  CampaignAreaPicker,
  totalAreaKm2,
} from '@features/campaigns/components/campaign-area-picker/campaign-area-picker';
import { TagInputComponent } from '@shared/components/tag-input/tag-input';
import type { Address } from '@shared/models/address.model';
import { ToastService } from '@shared/services/toast.service';
import { CurrentOrganizationStore } from '@features/organizations/store/current-organization.store';

export type AddressSource = 'plz' | 'area' | 'csv' | 'existing';

/** How many addresses the wizard shows as a sample; the rest stay in the draft. */
const ADDRESS_PREVIEW_SIZE = 10;

@Component({
  selector: 'app-create-campaign-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    NgIconComponent,
    HlmInputDirective,
    WizardDialogHeader,
    WizardDialogFooter,
    SuffixInput,
    AddressMultiSelect,
    FieldShell,
    SummarySection,
    SummaryRow,
    TextareaField,
    StepLead,
    ScrollingModule,
    HlmButtonDirective,
    HlmSeparatorDirective,
    ...BrnDialogImports,
    ...HlmDialogImports,
    TagInputComponent,
    CampaignAreaPicker,
  ],
  providers: [
    provideIcons({
      lucideArrowRight, lucideArrowLeft, lucideCheck, lucideMapPin,
      lucideFileSpreadsheet, lucideUpload, lucideTrash, lucideTriangleAlert,
      lucideCircleCheck, lucideRocket, lucideShieldAlert, lucidePhone,
      lucideX, lucideHash, lucidePlus, lucideInfo, lucideTag,
      lucideDownload, lucideLoader, lucideSearch, lucideBuilding, lucidePencilRuler,
    }),
  ],
  templateUrl: './create-campaign-dialog.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateCampaignDialog extends BaseCreationDialog {
  private readonly campaignsService = inject(CampaignsService);
  private readonly toast = inject(ToastService);
  private readonly currentOrgStore = inject(CurrentOrganizationStore);
  private readonly packageConfig = signal<Record<PackageType, PackageConfig> | null>(null);
  readonly isLoadingPackageConfig = signal(false);
  readonly packageConfigError = signal<string | null>(null);

  override config = input<BaseDialogConfig>({
    title: 'Neue Kampagne erstellen',
    description: 'Erstellen Sie eine neue Kampagne mit PLZ-Gebiet oder CSV-Upload',
    stepLabels: ['Quelle', 'Adressen', 'Validierung', 'Paket', 'Details', 'Übersicht'],
    stepLeads: [
      'Wählen Sie, woher die Adressen dieser Kampagne stammen.',
      'Erfassen Sie die Adressen für diese Kampagne.',
      'Prüfen Sie die erkannten Adressen vor dem Anlegen.',
      'Wählen Sie den Leistungsumfang der Kampagne.',
      'Name, Standard-Analysewerte und weitere Details der Kampagne.',
      'Prüfen Sie die Kampagne vor dem Erstellen.',
    ],
    submitButtonText: 'Kampagne erstellen',
    showTimeline: true,
    eyebrow: 'Kampagne',
  });

  organizationId = input.required<string>();

  // ─── Preselected mode (opens at step 2 with pre-chosen addresses) ─────────
  readonly preselectedMode = signal(false);

  override readonly wizardSteps = computed<TimelineStep[]>(() => {
    if (this.preselectedMode()) {
      const labels = ['Adressen', 'Validierung', 'Paket', 'Details', 'Übersicht'];
      return createWizardSteps(labels, this.currentStep() - 1);
    }
    return createWizardSteps(this.config().stepLabels, this.currentStep());
  });

  override readonly isFirstStep = computed(
    () => this.preselectedMode() ? this.currentStep() <= 2 : this.currentStep() === 1,
  );

  override prevStep(): void {
    if (this.preselectedMode() && this.currentStep() <= 2) return;
    super.prevStep();
  }

  // ─── Step 1: Source ───────────────────────────────────────────────────────
  /** The three ways addresses can enter a campaign, rendered as one list. */
  readonly addressSources: {
    key: AddressSource;
    icon: string;
    title: string;
    description: string;
  }[] = [
    {
      key: 'plz',
      icon: 'lucideMapPin',
      title: 'Nach Postleitzahl',
      description:
        'Geben Sie Postleitzahlen ein und wir laden automatisch alle Adressen in diesen Gebieten über die Solar Insight API.',
    },
    {
      key: 'area',
      icon: 'lucidePencilRuler',
      title: 'Gebiet auf der Karte',
      description:
        'Zeichnen Sie ein Gebiet ein, etwa einen Stadtteil. Wir laden alle Gebäudeadressen darin.',
    },
    {
      key: 'csv',
      icon: 'lucideFileSpreadsheet',
      title: 'CSV-Datei hochladen',
      description:
        'Laden Sie eine CSV-Datei mit den gewünschten Adressen hoch. Die Datei wird auf Gültigkeit geprüft.',
    },
    {
      key: 'existing',
      icon: 'lucideBuilding',
      title: 'Aus Adressbuch',
      description: 'Wählen Sie Adressen aus Ihrem Adressbuch aus.',
    },
  ];

  addressSource = signal<AddressSource | null>(null);

  // ─── Step 2a: PLZ / drawn area (both resolved through OpenStreetMap) ──────
  /** Finished areas from the area picker; kept when the user steps back. */
  areaPolygons = signal<[number, number][][]>([]);
  readonly areaKm2 = computed(() => totalAreaKm2(this.areaPolygons()));
  /** Addresses OSM had but without street/postal code, so they cannot become campaign entries. */
  areaSkipped = signal(0);
  plzInput = signal('');
  plzList = signal<string[]>([]);
  isLoadingPlzAddresses = signal(false);
  plzAddressCount = signal<number | null>(null);
  /** Step-2 input-validation error (invalid PLZ format). */
  plzError = signal<string | null>(null);
  /** Step-3 fetch error: OSM unreachable or zero addresses returned. */
  plzFetchError = signal<string | null>(null);

  // ─── Elapsed-time feedback for the load/validate phase ────────────────────
  private readonly loadElapsedMs = signal(0);
  private loadTimerHandle: ReturnType<typeof setInterval> | null = null;
  private loadStartedAt = 0;

  /** `m:ss` label for the current load/validate elapsed time. */
  readonly loadElapsedLabel = computed(() => {
    const totalSec = Math.floor(this.loadElapsedMs() / 1000);
    const m = Math.floor(totalSec / 60);
    const s = totalSec % 60;
    return `${m}:${s.toString().padStart(2, '0')}`;
  });

  // ─── Step 2b: CSV ─────────────────────────────────────────────────────────
  selectedFile = signal<File | null>(null);
  isDragOver = signal(false);
  isCsvUploading = signal(false);
  uploadError = signal<string | null>(null);
  csvValidation = signal<CsvValidationResult | null>(null);

  // ─── Step 2c: Existing addresses ─────────────────────────────────────────────
  existingAddresses = signal<Address[]>([]);
  selectedExistingIds = signal<Set<string>>(new Set());
  addressSearchQuery = signal('');
  isLoadingExistingAddresses = signal(false);
  existingAddressesError = signal<string | null>(null);

  readonly filteredExistingAddresses = computed(() => {
    const q = this.addressSearchQuery().toLowerCase();
    if (!q) return this.existingAddresses();
    return this.existingAddresses().filter(
      (a) =>
        a.street?.toLowerCase().includes(q) ||
        a.city?.toLowerCase().includes(q) ||
        a.postalCode?.toLowerCase().includes(q),
    );
  });

  readonly selectedExistingCount = computed(() => this.selectedExistingIds().size);

  /** How the addresses got here, phrased for the summary table. */
  readonly sourceSummaryLine = computed(() => {
    switch (this.addressSource()) {
      case 'plz':
        return `Postleitzahlen (${this.plzList().join(', ')})`;
      case 'area':
        return `${this.areaPolygons().length === 1 ? 'Gezeichnetes Gebiet' : `${this.areaPolygons().length} gezeichnete Gebiete`} (${this.areaKm2().toLocaleString('de-DE', { maximumFractionDigits: 2 })} km²)`;
      case 'existing':
        return `Adressbuch (${this.selectedExistingCount()} Adressen)`;
      case 'csv':
        return `CSV-Upload (${this.selectedFile()?.name ?? '—'})`;
      default:
        return '—';
    }
  });

  /** Jump back to an already-reached step from a summary section's Ändern link. */
  goToStepNumber(target: number): void {
    this.jumpToStep(target);
  }

  /** Picked rows as full objects, so the selection list survives filtering. */
  readonly selectedExistingAddresses = computed(() => {
    const ids = this.selectedExistingIds();
    return this.existingAddresses().filter((a) => ids.has(a.addressId));
  });

  protected trackByAddressId(_index: number, address: Address): string {
    return address.addressId;
  }

  // ─── Shared: address preview (set after step 2 for either source) ─────────
  /**
   * A short sample for display only. The full list lives in the server-side
   * draft — a PLZ area can hold tens of thousands of addresses, and shipping
   * them into the browser just to count them is what made this step crawl.
   */
  validatedAddresses = signal<AddressPreview[]>([]);
  /** Authoritative address count from the server (the draft, not the preview). */
  addressCount = signal(0);
  /** Draft ID returned by resolve-postal-codes or validate-csv. */
  draftId = signal<string | null>(null);

  // ─── Step 3: SI validation ─────────────────────────────────────────────────
  isValidatingAddresses = signal(false);
  siValidation = signal<SiValidationResult | null>(null);
  siValidationError = signal<string | null>(null);
  /** Draft ID returned by validate-area — contains only addresses that passed. */
  validatedDraftId = signal<string | null>(null);
  /** Addresses filtered out due to bad OSM coordinates (>2° from median). */
  coordinateOutliers = signal<OutlierAddress[]>([]);

  // ─── Step 4: Details ──────────────────────────────────────────────────────
  campaignTitle = signal('');
  campaignDescription = signal('');
  campaignNotes = signal('');
  tags = signal<string[]>([]);
  defaultAnnualConsumptionKwh = signal<number | null>(null);
  defaultElectricityPriceEurPerKwh = signal<number | null>(null);

  // ─── Computed ─────────────────────────────────────────────────────────────

  readonly totalAddresses = computed(() => this.addressCount());

  /** Addresses that passed SI validation — the campaign is created from the draft. */
  readonly effectiveAddresses = computed(() =>
    Math.max(0, this.addressCount() - (this.siValidation()?.failedCount ?? 0)),
  );

  /** Combined failure list: SI geocoding failures + outlier addresses */
  readonly allFailures = computed(() => {
    const siFail = (this.siValidation()?.failures ?? []).map((f) => ({
      label: `Zeile ${f.index + 1}`,
      reason: f.detail,
    }));
    const outlierFail = this.coordinateOutliers().map((o) => ({
      label: 'Koordinaten',
      reason: `${o.street} ${o.houseNumber}, ${o.postalCode} ${o.city}`,
    }));
    return [...siFail, ...outlierFail];
  });

  readonly totalFailedCount = computed(
    () => (this.siValidation()?.failedCount ?? 0) + this.coordinateOutliers().length,
  );

  /** Top geographic outliers when area validation fails (existing-address mode only). */
  readonly areaOutliers = computed((): (Address & { distKm: number })[] => {
    if (this.addressSource() !== 'existing' || this.siValidation()?.areaOk !== false) return [];
    const selectedIds = this.selectedExistingIds();
    const selected = this.existingAddresses().filter(
      (a) => selectedIds.has(a.addressId) && a.latitude != null && a.longitude != null,
    );
    if (selected.length < 2) return [];
    const centLat = selected.reduce((s, a) => s + a.latitude!, 0) / selected.length;
    const centLon = selected.reduce((s, a) => s + a.longitude!, 0) / selected.length;
    return selected
      .map((a) => ({ ...a, distKm: this.haversine(centLat, centLon, a.latitude!, a.longitude!) }))
      .sort((a, b) => b.distKm - a.distKm)
      .slice(0, 5);
  });

  readonly selectedPackage = computed(() => {
    const config = this.packageConfig();
    if (!config) return null;
    const count = this.effectiveAddresses();
    return (Object.entries(config) as [PackageType, PackageConfig][]).find(
      ([, item]) => count >= item.min && (item.max === null || count <= item.max),
    ) ?? null;
  });

  readonly packageMeta = computed((): PackageMeta | null => {
    const selected = this.selectedPackage();
    if (!selected) return null;
    const [, config] = selected;
    return {
      label: config.label,
      description: config.description,
      addressRange: formatAddressRange(config),
      priceRange: 'Preis wird separat bestätigt',
    };
  });

  readonly packageSelectionError = computed(() => {
    if (this.isLoadingPackageConfig()) return 'Paketpreise werden geladen…';
    if (this.packageConfigError()) return this.packageConfigError();
    const config = this.packageConfig();
    const count = this.effectiveAddresses();
    if (!config || count === 0) return null;
    const minimum = Math.min(...Object.values(config).map((item) => item.min));
    if (count < minimum) {
      return `Für eine Kampagne sind mindestens ${minimum.toLocaleString('de-DE')} validierte Adressen erforderlich.`;
    }
    return null;
  });

  override open(): void {
    void this.loadPackageConfig();
    super.open();
  }

  // ─── Step navigation ──────────────────────────────────────────────────────

  override canProceedFromCurrentStep(): boolean {
    switch (this.currentStep()) {
      case 1: return this.addressSource() !== null;
      case 2:
        if (this.addressSource() === 'plz') return this.plzList().length > 0;
        if (this.addressSource() === 'area')
          return this.areaPolygons().length > 0 && this.areaKm2() <= AREA_MAX_KM2;
        if (this.addressSource() === 'existing') return this.selectedExistingCount() > 0;
        return this.selectedFile() !== null;
      case 3: {
        if (this.isCsvUploading() || this.isLoadingPlzAddresses() || this.isValidatingAddresses()) return false;
        if (this.plzFetchError()) return false;
        if (this.addressSource() === 'csv' && this.uploadError()) return false;
        if (this.addressSource() === 'csv' && this.csvValidation() && this.totalAddresses() === 0) return false;
        if (this.siValidationError() && this.totalAddresses() > 0) return true;
        if (!this.siValidation()?.areaOk) return false;
        return this.effectiveAddresses() > 0 && !!this.selectedPackage() && !this.packageSelectionError();
      }
      case 4: return !!this.selectedPackage() && !this.packageSelectionError();
      case 5:
      case 6:
        return this.campaignTitle().trim().length > 0 && this.hasValidAnalysisDefaults();
      default: return false;
    }
  }

  override nextStep(): void {
    if (!this.canProceedFromCurrentStep()) return;
    if (this.currentStep() === 2) {
      super.nextStep();
      this.loadAndValidate();
      return;
    }
    super.nextStep();
  }

  /** Entry point when entering step 3: fetch/parse addresses then SI validate */
  async loadAndValidate(): Promise<void> {
    // Results of an earlier pass describe other addresses; never show them during a new load.
    this.siValidation.set(null);
    this.siValidationError.set(null);
    this.validatedDraftId.set(null);
    this.startLoadTimer();
    try {
      if (this.addressSource() === 'plz' || this.addressSource() === 'area') {
        await this.lookupPlzAddresses();
        if (this.plzFetchError()) return;
      } else if (this.addressSource() === 'csv') {
        await this.uploadAndValidateCsv();
        if (this.uploadError() || this.validatedAddresses().length === 0) return;
      } else if (this.addressSource() === 'existing') {
        await this.storeExistingAddressDraft();
        if (this.existingAddressesError()) return;
      }
      await this.runSiValidation();
    } catch (err) {
      // Safety net: reset all loading flags so the UI is never stuck spinning.
      this.isCsvUploading.set(false);
      this.isLoadingPlzAddresses.set(false);
      this.isValidatingAddresses.set(false);
      this.uploadError.set(err instanceof Error ? err.message : 'Unbekannter Fehler bei der Validierung.');
      console.error('[loadAndValidate]', err);
    } finally {
      this.stopLoadTimer();
    }
  }

  private startLoadTimer(): void {
    this.stopLoadTimer();
    this.loadStartedAt = Date.now();
    this.loadElapsedMs.set(0);
    this.loadTimerHandle = setInterval(() => {
      this.loadElapsedMs.set(Date.now() - this.loadStartedAt);
    }, 250);
  }

  private stopLoadTimer(): void {
    if (this.loadTimerHandle !== null) {
      clearInterval(this.loadTimerHandle);
      this.loadTimerHandle = null;
    }
  }

  protected override resetDialog(): void {
    super.resetDialog();
    this.stopLoadTimer();
    this.loadElapsedMs.set(0);
    this.preselectedMode.set(false);
    this.addressSource.set(null);
    this.plzInput.set('');
    this.plzList.set([]);
    this.plzAddressCount.set(null);
    this.plzError.set(null);
    this.plzFetchError.set(null);
    this.isLoadingPlzAddresses.set(false);
    this.areaPolygons.set([]);
    this.areaSkipped.set(0);
    this.selectedFile.set(null);
    this.isDragOver.set(false);
    this.isCsvUploading.set(false);
    this.uploadError.set(null);
    this.csvValidation.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
    this.draftId.set(null);
    this.isValidatingAddresses.set(false);
    this.siValidation.set(null);
    this.siValidationError.set(null);
    this.validatedDraftId.set(null);
    this.coordinateOutliers.set([]);
    this.existingAddresses.set([]);
    this.selectedExistingIds.set(new Set());
    this.addressSearchQuery.set('');
    this.isLoadingExistingAddresses.set(false);
    this.existingAddressesError.set(null);
    this.campaignTitle.set('');
    this.campaignDescription.set('');
    this.campaignNotes.set('');
    this.tags.set([]);
    this.defaultAnnualConsumptionKwh.set(null);
    this.defaultElectricityPriceEurPerKwh.set(null);
  }

  protected override async handleSubmit(_dialogContext: unknown): Promise<void> {
    const draftId = this.validatedDraftId() ?? this.draftId();
    const packageType = this.selectedPackage()?.[0];
    if (!draftId) {
      throw new Error('Keine Adressen gespeichert. Bitte starten Sie den Assistenten neu.');
    }
    if (!this.hasValidAnalysisDefaults()) {
      throw new Error('Bitte geben Sie einen gültigen Jahresstromverbrauch und Strompreis ein.');
    }
    if (!packageType) {
      throw new Error(this.packageSelectionError() ?? 'Kein gültiges Kampagnenpaket gefunden.');
    }

    const dto: CreateCampaignDto = {
      title: this.campaignTitle().trim(),
      organizationId: this.organizationId(),
      defaultAnnualConsumptionKwh: this.defaultAnnualConsumptionKwh()!,
      defaultElectricityPriceEurPerKwh: this.defaultElectricityPriceEurPerKwh()!,
      description: this.campaignDescription().trim() || undefined,
      packageType,
      targetAnalyses: this.effectiveAddresses(),
      tags: this.tags().length > 0 ? this.tags() : undefined,
      notes: this.campaignNotes().trim() || undefined,
      metadata: {
        addressSource: this.addressSource(),
        plzList: this.addressSource() === 'plz' ? this.plzList() : undefined,
        areaPolygons: this.addressSource() === 'area' ? this.areaPolygons() : undefined,
        csvFileName: this.addressSource() === 'csv' ? this.selectedFile()?.name : undefined,
        existingAddressCount: this.addressSource() === 'existing' ? this.selectedExistingCount() : undefined,
      },
      draftId,
    };

    const campaign = await this.campaignsService.createCampaign(this.organizationId(), dto);

    this.toast.success(`Kampagne „${dto.title}“ wurde erstellt`);

    this.submitted.emit(campaign);
  }

  private hasValidAnalysisDefaults(): boolean {
    return (
      (this.defaultAnnualConsumptionKwh() ?? 0) > 0 &&
      (this.defaultElectricityPriceEurPerKwh() ?? 0) > 0
    );
  }

  private async loadPackageConfig(): Promise<void> {
    this.isLoadingPackageConfig.set(true);
    this.packageConfigError.set(null);
    try {
      this.packageConfig.set(await this.campaignsService.getPackageConfig(this.organizationId()));
    } catch {
      this.packageConfig.set(null);
      this.packageConfigError.set('Paketpreise konnten nicht geladen werden. Bitte versuchen Sie es erneut.');
    } finally {
      this.isLoadingPackageConfig.set(false);
    }
  }

  /**
   * Opens the dialog pre-populated with addresses from an external selection.
   * Skips source selection — timeline shows Adressen → Validierung → … only.
   */
  openWithPreselectedAddresses(addresses: Address[]): void {
    this.resetDialog();
    this.preselectedMode.set(true);
    this.addressSource.set('existing');
    this.existingAddresses.set(addresses);
    this.selectedExistingIds.set(new Set(addresses.map((a) => a.addressId)));
    this.currentStep.set(2);
    this.open();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step 1: Source
  // ═══════════════════════════════════════════════════════════════════════════

  selectSource(source: AddressSource): void {
    // Re-picking the current source after "Zurück" must not wipe what was entered.
    if (source === this.addressSource()) return;
    this.addressSource.set(source);
    this.areaPolygons.set([]);
    this.plzList.set([]);
    this.plzAddressCount.set(null);
    this.plzError.set(null);
    this.plzFetchError.set(null);
    this.selectedFile.set(null);
    this.csvValidation.set(null);
    this.uploadError.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
    this.draftId.set(null);
    this.validatedDraftId.set(null);
    this.siValidation.set(null);
    this.siValidationError.set(null);
    this.coordinateOutliers.set([]);
    this.existingAddresses.set([]);
    this.selectedExistingIds.set(new Set());
    this.addressSearchQuery.set('');
    this.existingAddressesError.set(null);
    if (source === 'existing') this.loadExistingAddresses();
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step 2c: Existing addresses
  // ═══════════════════════════════════════════════════════════════════════════

  async loadExistingAddresses(): Promise<void> {
    if (this.isLoadingExistingAddresses()) return;
    this.isLoadingExistingAddresses.set(true);
    this.existingAddressesError.set(null);
    try {
      const { data } = await this.campaignsService.getOrganizationAddresses(this.organizationId(), { limit: 50_000 });
      this.existingAddresses.set(data);
    } catch (err) {
      this.existingAddressesError.set(err instanceof Error ? err.message : 'Fehler beim Laden der Adressen.');
    } finally {
      this.isLoadingExistingAddresses.set(false);
    }
  }

  toggleExistingAddress(addressId: string): void {
    this.selectedExistingIds.update((ids) => {
      const next = new Set(ids);
      if (next.has(addressId)) next.delete(addressId);
      else next.add(addressId);
      return next;
    });
  }

  toggleAllExistingAddresses(): void {
    const filtered = this.filteredExistingAddresses();
    const selectedIds = this.selectedExistingIds();
    const allSelected = filtered.every((a) => selectedIds.has(a.addressId));
    this.selectedExistingIds.update((ids) => {
      const next = new Set(ids);
      if (allSelected) filtered.forEach((a) => next.delete(a.addressId));
      else filtered.forEach((a) => next.add(a.addressId));
      return next;
    });
  }

  readonly allFilteredSelected = computed(() => {
    const filtered = this.filteredExistingAddresses();
    const ids = this.selectedExistingIds();
    return filtered.length > 0 && filtered.every((a) => ids.has(a.addressId));
  });

  async storeExistingAddressDraft(): Promise<void> {
    const selectedIds = this.selectedExistingIds();
    const addresses = this.existingAddresses().filter((a) => selectedIds.has(a.addressId));
    if (addresses.length === 0) return;
    this.existingAddressesError.set(null);
    try {
      const result = await this.campaignsService.storeAddressDraft(
        this.organizationId(),
        addresses.map((a) => ({
          street: a.street,
          houseNumber: a.houseNumber,
          postalCode: a.postalCode,
          city: a.city,
          country: a.country ?? 'DE',
          latitude: a.latitude,
          longitude: a.longitude,
        })),
      );
      this.addressCount.set(addresses.length);
      this.validatedAddresses.set(addresses.slice(0, ADDRESS_PREVIEW_SIZE).map((a) => ({
        street: a.street,
        houseNumber: a.houseNumber,
        postalCode: a.postalCode,
        city: a.city,
        country: a.country ?? 'DE',
        latitude: a.latitude,
        longitude: a.longitude,
      }) as AddressPreview));
      this.draftId.set(result.draftId);
      this.validatedDraftId.set(null);
    } catch (err) {
      this.existingAddressesError.set(err instanceof Error ? err.message : 'Fehler beim Speichern der Adressen.');
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step 2a: PLZ
  // ═══════════════════════════════════════════════════════════════════════════

  addPlz(): void {
    const raw = this.plzInput().trim();
    if (!raw) return;
    const entries = raw.split(/[,;\s]+/).map((s) => s.trim()).filter((s) => /^\d{5}$/.test(s));
    if (entries.length === 0) {
      this.plzError.set('Bitte geben Sie gültige 5-stellige Postleitzahlen ein.');
      return;
    }
    const current = this.plzList();
    const newEntries = entries.filter((e) => !current.includes(e));
    if (newEntries.length > 0) this.plzList.update((list) => [...list, ...newEntries]);
    this.plzInput.set('');
    this.plzError.set(null);
    this.plzFetchError.set(null);
    this.plzAddressCount.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
    this.draftId.set(null);
    this.validatedDraftId.set(null);
    this.coordinateOutliers.set([]);
  }

  removePlz(plz: string): void {
    this.plzList.update((list) => list.filter((p) => p !== plz));
    this.plzFetchError.set(null);
    this.plzAddressCount.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
    this.draftId.set(null);
    this.validatedDraftId.set(null);
    this.coordinateOutliers.set([]);
  }

  onPlzKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter') { event.preventDefault(); this.addPlz(); }
  }

  /** Loads the OSM addresses for the chosen postal codes or the drawn area into a draft. */
  async lookupPlzAddresses(): Promise<void> {
    const plzList = this.plzList();
    const area = this.addressSource() === 'area';
    if (area ? this.areaPolygons().length === 0 : plzList.length === 0) return;
    this.isLoadingPlzAddresses.set(true);
    this.plzError.set(null);
    this.plzFetchError.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
    this.draftId.set(null);
    this.validatedDraftId.set(null);
    this.coordinateOutliers.set([]);
    try {
      const result = area
        ? await this.campaignsService.fetchAreaAddresses(this.organizationId(), this.areaPolygons())
        : await this.campaignsService.fetchPostalCodeAddresses(this.organizationId(), plzList);
      this.areaSkipped.set('skippedIncomplete' in result ? Number(result.skippedIncomplete) : 0);
      this.plzAddressCount.set(result.totalAddresses);
      this.addressCount.set(result.totalAddresses);
      this.validatedAddresses.set(result.preview ?? []);
      this.draftId.set(result.draftId);
      this.coordinateOutliers.set(result.outliers ?? []);
      if ((result.totalAddresses ?? 0) === 0) {
        this.plzFetchError.set(
          area
            ? 'In diesem Gebiet wurden keine Gebäudeadressen in OpenStreetMap gefunden.'
            : 'Für diese Postleitzahl(en) wurden keine Gebäudeadressen in OpenStreetMap gefunden.',
        );
      }
    } catch (err) {
      this.plzFetchError.set(
        err instanceof Error ? err.message : 'Fehler beim Laden der Adressen für diese PLZ.',
      );
    } finally {
      this.isLoadingPlzAddresses.set(false);
    }
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step 2b: CSV
  // ═══════════════════════════════════════════════════════════════════════════

  onDragOver(event: DragEvent): void { event.preventDefault(); event.stopPropagation(); this.isDragOver.set(true); }
  onDragLeave(event: DragEvent): void { event.preventDefault(); event.stopPropagation(); this.isDragOver.set(false); }

  onDrop(event: DragEvent): void {
    event.preventDefault(); event.stopPropagation(); this.isDragOver.set(false);
    const file = event.dataTransfer?.files[0];
    if (file) this.handleFileSelection(file);
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.handleFileSelection(file);
  }

  private handleFileSelection(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.uploadError.set('Bitte wählen Sie eine CSV-Datei aus.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.uploadError.set('Die Datei darf maximal 10 MB groß sein.');
      return;
    }
    this.selectedFile.set(file);
    this.uploadError.set(null);
    this.csvValidation.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
  }

  clearFile(): void {
    this.selectedFile.set(null);
    this.csvValidation.set(null);
    this.uploadError.set(null);
    this.validatedAddresses.set([]);
    this.addressCount.set(0);
  }

  async uploadAndValidateCsv(): Promise<void> {
    const file = this.selectedFile();
    if (!file) return;
    this.isCsvUploading.set(true);
    this.uploadError.set(null);
    try {
      const result = await this.campaignsService.validateCsv(this.organizationId(), file);
      this.csvValidation.set(result);
      this.addressCount.set(result.validAddresses);
      this.validatedAddresses.set(
        (result.preview ?? result.addresses ?? []).slice(0, ADDRESS_PREVIEW_SIZE),
      );
      this.draftId.set(result.draftId);
      this.validatedDraftId.set(null);
    } catch (err) {
      this.uploadError.set(
        err instanceof Error ? err.message : 'Fehler beim Validieren der CSV-Datei.',
      );
    } finally {
      this.isCsvUploading.set(false);
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  async downloadTemplate(): Promise<void> {
    await this.campaignsService.downloadCsvTemplate(this.organizationId());
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // Step 3: SI validation
  // ═══════════════════════════════════════════════════════════════════════════

  async runSiValidation(): Promise<void> {
    const draftId = this.draftId();
    if (!draftId) return;
    this.isValidatingAddresses.set(true);
    this.siValidation.set(null);
    this.siValidationError.set(null);
    this.validatedDraftId.set(null);
    try {
      const result = await this.campaignsService.validateAddresses(this.organizationId(), draftId);
      this.siValidation.set(result);
      this.validatedDraftId.set(result.validatedDraftId);
    } catch (err) {
      this.siValidationError.set(
        err instanceof Error ? err.message : 'Fehler bei der SolarInsight-Validierung.',
      );
    } finally {
      this.isValidatingAddresses.set(false);
    }
  }

  async retryValidation(): Promise<void> {
    await this.loadAndValidate();
  }

  async removeOutlierAndRevalidate(addressId: string): Promise<void> {
    this.selectedExistingIds.update((ids) => {
      const next = new Set(ids);
      next.delete(addressId);
      return next;
    });
    await this.loadAndValidate();
  }

  private haversine(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a =
      Math.sin(dLat / 2) ** 2 +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) ** 2;
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  }

}
